/**
 * Client Projects Service
 *
 * Frontend service layer for the Client Projects Workspace.
 * All methods accept an optional AbortSignal for cleanup on component unmount.
 *
 * Requirements: 1.1, 6.4, 13.5
 */

import type {
  AttentionItemType,
  ClientProjectsListResponse,
  ClientProjectDetailResponse,
  ClientActivityPageResponse,
  ClientApproveResponse,
  ClientRevisionRequestResponse,
  ClientDismissAttentionResponse,
} from '../types/client-workspace';
import {
  readAdminApiJsonOrThrow,
  ViewerRequestError,
} from './adminApiClient';
import { buildApiUrl } from './apiUrl';
import { authenticatedFetch } from '../lib/apiClient';

// --- Typed ConflictError for 409 responses ---

export class ConflictError extends ViewerRequestError {
  constructor(message: string, code: string | null = null) {
    super(409, message, code);
    this.name = 'ConflictError';
  }
}

// --- Internal helpers ---

const CLIENT_API_BASE = '/api/client/projects';

function buildClientApiUrl(path: string): string {
  return buildApiUrl(`${CLIENT_API_BASE}${path}`);
}

/**
 * Wraps readAdminApiJsonOrThrow to additionally handle 409 Conflict
 * as a typed ConflictError rather than a generic ViewerRequestError.
 */
async function readClientApiJsonOrThrow<T extends { success: true }>(
  response: Response,
): Promise<T> {
  if (response.status === 409) {
    let message = 'Project status has changed. Please refresh and try again.';
    let code: string | null = 'CONFLICT';

    try {
      const payload = await response.json();
      if (payload?.error?.message) {
        message = payload.error.message;
      }
      if (payload?.error?.code) {
        code = payload.error.code;
      }
    } catch {
      // Use default message if JSON parsing fails
    }

    throw new ConflictError(message, code);
  }

  return readAdminApiJsonOrThrow<T & { success: true }>(response);
}

// --- Service ---

export const clientProjectsService = {
  /**
   * Fetch the client's project list with attention items and status counts.
   */
  async listProjects(signal?: AbortSignal): Promise<ClientProjectsListResponse> {
    const response = await authenticatedFetch(buildClientApiUrl(''), {
      method: 'GET',
      signal,
    });

    return readClientApiJsonOrThrow<ClientProjectsListResponse>(response);
  },

  /**
   * Fetch full project detail including activity feed.
   */
  async getProjectDetail(
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ClientProjectDetailResponse> {
    const response = await authenticatedFetch(
      buildClientApiUrl(`/${encodeURIComponent(projectId)}`),
      {
        method: 'GET',
        signal,
      },
    );

    return readClientApiJsonOrThrow<ClientProjectDetailResponse>(response);
  },

  /**
   * Fetch a paginated page of activity entries for a project.
   */
  async getActivityPage(
    projectId: string,
    cursor: string | null,
    signal?: AbortSignal,
  ): Promise<ClientActivityPageResponse> {
    const params = new URLSearchParams({ limit: '20' });
    if (cursor) {
      params.set('cursor', cursor);
    }

    const response = await authenticatedFetch(
      buildClientApiUrl(`/${encodeURIComponent(projectId)}/activity?${params.toString()}`),
      {
        method: 'GET',
        signal,
      },
    );

    return readClientApiJsonOrThrow<ClientActivityPageResponse>(response);
  },

  /**
   * Approve a deliverable. Only succeeds if project status is 'awaiting_review'.
   * Throws ConflictError (409) if the status has already changed.
   */
  async approveDeliverable(
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ClientApproveResponse> {
    const response = await authenticatedFetch(
      buildClientApiUrl(`/${encodeURIComponent(projectId)}/approve`),
      {
        method: 'POST',
        signal,
      },
    );

    return readClientApiJsonOrThrow<ClientApproveResponse>(response);
  },

  /**
   * Request a revision on a deliverable with a comment.
   * Throws ConflictError (409) if the status has already changed.
   * Rejects empty comments client-side before making the request.
   */
  async requestRevision(
    projectId: string,
    comment: string,
    signal?: AbortSignal,
  ): Promise<ClientRevisionRequestResponse> {
    const trimmed = comment.trim();
    if (!trimmed) {
      throw new ViewerRequestError(400, 'Comment is required.', 'INVALID_INPUT');
    }

    const response = await authenticatedFetch(
      buildClientApiUrl(`/${encodeURIComponent(projectId)}/revision`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: trimmed }),
        signal,
      },
    );

    return readClientApiJsonOrThrow<ClientRevisionRequestResponse>(response);
  },

  /**
   * Dismiss an attention item for a project. Idempotent.
   */
  async dismissAttentionItem(
    projectId: string,
    type: AttentionItemType,
    signal?: AbortSignal,
  ): Promise<ClientDismissAttentionResponse> {
    const response = await authenticatedFetch(
      buildClientApiUrl(`/${encodeURIComponent(projectId)}/dismiss-attention`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
        signal,
      },
    );

    return readClientApiJsonOrThrow<ClientDismissAttentionResponse>(response);
  },
};
